import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Link2 } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { QUALITY_FLAGS_LABELS } from "@/constants/wpQualityFlags";

interface WpPostPreviewModalProps {
  postId: string | null;
  onClose: () => void;
}

interface WpPostFull {
  id: string;
  wp_post_id: number;
  title: string;
  slug: string | null;
  wp_url: string | null;
  published_at: string | null;
  modified_at: string | null;
  content: string | null;
  content_plaintext: string | null;
  excerpt: string | null;
  meta_description: string | null;
  featured_image_url: string | null;
  word_count: number | null;
  h2_count: number | null;
  h3_count: number | null;
  image_count: number | null;
  outbound_links_count: number | null;
  internal_links_count: number | null;
  quality_score: number | null;
  quality_recommendation: string | null;
  quality_red_flags: string[];
  keyword: string | null;
  categories: Array<{ id: number; name: string }>;
  tags: Array<{ id: number; name: string }>;
  first_100_words: string | null;
  // M9 SRO
  sro_extracted_at: string | null;
  extracted_entity: string | null;
  extracted_attribute: string | null;
  extracted_query_frame: string | null;
  first_paragraph_answer: string | null;
  supporting_entities: string[];
  // M12 Quality Audit LLM
  entity_anchoring_score: number | null;
  information_gain_score: number | null;
  humanization_risk_score: number | null;
  retrieval_ready: boolean | null;
  quality_audit_issues: string[];
  quality_audit_verdict: string | null;
  quality_audited_at: string | null;
  // M14 Cluster Fit
  cluster_fit: string | null;
  cluster_fit_confidence: number | null;
  cluster_fit_reason: string | null;
  cluster_fit_classified_at: string | null;
  cluster_fit_method: string | null;
  // M13 SEO (Senuto)
  seo_organic_traffic: number | null;
  seo_total_keywords: number | null;
  seo_top_3_count: number | null;
  seo_top_10_count: number | null;
  seo_top_50_count: number | null;
  seo_best_keyword: string | null;
  seo_best_position: number | null;
  seo_best_keyword_volume: number | null;
  seo_gained_keywords_count: number | null;
  seo_lost_keywords_count: number | null;
  seo_content_decay: boolean | null;
  seo_has_cannibalization: boolean | null;
  seo_cannibalization_partner_url: string | null;
  seo_all_keywords: Array<{
    keyword: string;
    position: number;
    previous_position: number;
    volume: number;
    difficulty: number | null;
    snippets: string[];
  }>;
  seo_last_sync: string | null;
}

interface MatchedNode {
  id: string;
  title: string;
  slug: string | null;
  map_id: string;
}

async function fetchWpPostFull(postId: string): Promise<WpPostFull | null> {
  const { data, error } = await supabase
    .from("wp_posts")
    .select(
      "id, wp_post_id, title, slug, wp_url, published_at, modified_at, content, content_plaintext, excerpt, meta_description, featured_image_url, word_count, h2_count, h3_count, image_count, outbound_links_count, internal_links_count, quality_score, quality_recommendation, quality_red_flags, keyword, categories, tags, first_100_words, sro_extracted_at, extracted_entity, extracted_attribute, extracted_query_frame, first_paragraph_answer, supporting_entities, entity_anchoring_score, information_gain_score, humanization_risk_score, retrieval_ready, quality_audit_issues, quality_audit_verdict, quality_audited_at, seo_organic_traffic, seo_total_keywords, seo_top_3_count, seo_top_10_count, seo_top_50_count, seo_best_keyword, seo_best_position, seo_best_keyword_volume, seo_gained_keywords_count, seo_lost_keywords_count, seo_content_decay, seo_has_cannibalization, seo_cannibalization_partner_url, seo_all_keywords, seo_last_sync, cluster_fit, cluster_fit_confidence, cluster_fit_reason, cluster_fit_classified_at, cluster_fit_method",
    )
    .eq("id", postId)
    .maybeSingle();
  if (error || !data) return null;
  const row = data as unknown as WpPostFull & {
    quality_red_flags?: unknown;
    categories?: unknown;
    tags?: unknown;
    supporting_entities?: unknown;
    quality_audit_issues?: unknown;
  };
  return {
    ...row,
    quality_red_flags: Array.isArray(row.quality_red_flags) ? (row.quality_red_flags as string[]) : [],
    categories: Array.isArray(row.categories)
      ? (row.categories as Array<{ id: number; name: string }>)
      : [],
    tags: Array.isArray(row.tags) ? (row.tags as Array<{ id: number; name: string }>) : [],
    supporting_entities: Array.isArray(row.supporting_entities) ? (row.supporting_entities as string[]) : [],
    quality_audit_issues: Array.isArray(row.quality_audit_issues) ? (row.quality_audit_issues as string[]) : [],
    seo_all_keywords: Array.isArray((row as unknown as { seo_all_keywords?: unknown }).seo_all_keywords)
      ? ((row as unknown as { seo_all_keywords: WpPostFull["seo_all_keywords"] }).seo_all_keywords)
      : [],
  };
}

const QUERY_FRAME_EXPLAIN: Record<string, { label: string; desc: string; color: string }> = {
  definitional: { label: "Definicyjne", desc: "Odpowiada na pytanie 'co to jest X' — definicja, pochodzenie, istota", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  procedural: { label: "Proceduralne", desc: "Odpowiada na pytanie 'jak zrobić X' — kroki, instrukcja, procedura", color: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30" },
  comparative: { label: "Porównawcze", desc: "'X vs Y', 'czym różni się A od B' — porównanie, wybór", color: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  causal: { label: "Przyczynowe", desc: "'dlaczego X', 'co powoduje X' — analiza przyczyny/skutku", color: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30" },
  evaluative: { label: "Ewaluacyjne", desc: "'czy warto X', 'najlepsze X' — ocena, ranking, opinia", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  mixed: { label: "Mieszane", desc: "Wiele intencji naraz — ogólny artykuł przeglądowy", color: "bg-slate-500/15 text-slate-700 dark:text-slate-400 border-slate-500/30" },
};

async function fetchMatchedNode(postId: string): Promise<MatchedNode | null> {
  const { data } = await supabase
    .from("thematic_nodes")
    .select("id, title, slug, map_id")
    .eq("matched_wp_post_id", postId)
    .limit(1)
    .maybeSingle();
  return (data as MatchedNode | null) ?? null;
}

function QualityBadge({
  score,
  recommendation,
}: {
  score: number | null;
  recommendation: string | null;
}) {
  if (score === null || score === undefined) {
    return <Badge variant="outline">Nie oceniono</Badge>;
  }
  const color =
    recommendation === "keep"
      ? "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30"
      : recommendation === "borderline"
        ? "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30"
        : "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30";
  return <Badge className={`${color} border`}>{score}/100</Badge>;
}

function RecBadge({ rec }: { rec: string | null }) {
  if (!rec) return null;
  const cfg: Record<string, { label: string; color: string }> = {
    keep: { label: "Zachowaj", color: "bg-green-500/15 text-green-700 dark:text-green-400" },
    borderline: {
      label: "Borderline",
      color: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
    },
    refurbish: { label: "Przebudować", color: "bg-red-500/15 text-red-700 dark:text-red-400" },
  };
  const c = cfg[rec] || { label: rec, color: "bg-muted" };
  return <Badge className={c.color}>{c.label}</Badge>;
}

function formatRel(ts: string | null): string {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return `${format(d, "dd.MM.yyyy", { locale: pl })} (${formatDistanceToNow(d, { addSuffix: true, locale: pl })})`;
  } catch {
    return ts;
  }
}

/**
 * Sanitize content_html dla iframe srcDoc z sandbox="".
 * Dodajemy <base target="_parent"> aby linki otwieraly sie w nowej karcie parenta
 * (sandbox="" bez allow-popups i tak je blokuje — user zobaczy link jako tekst).
 * sandbox="" daje pelna izolacje: zero JS, zero dostepu do parent, zero cookies,
 * zero localStorage, zero form submissions. CSS dziala ale w izolowanym origin.
 */
function buildSafeSrcDoc(html: string): string {
  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <base target="_blank">
    <style>
      body { font-family: system-ui, -apple-system, sans-serif; margin: 16px; color: #111; background: #fff; line-height: 1.6; }
      img { max-width: 100%; height: auto; }
      pre, code { white-space: pre-wrap; word-break: break-word; }
      table { max-width: 100%; display: block; overflow-x: auto; }
    </style>
  </head><body>${html}</body></html>`;
}

/**
 * Score badge dla Quality Audit. Dla inverted=true (humanization_risk)
 * niskie wartości są zielone (dobrze), wysokie czerwone (AI-generic).
 */
function AuditScore({
  label,
  score,
  hint,
  inverted = false,
}: {
  label: string;
  score: number | null;
  hint?: string;
  inverted?: boolean;
}) {
  if (score === null || score === undefined) {
    return (
      <div>
        <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
        <span className="text-xs text-muted-foreground">—</span>
      </div>
    );
  }
  // Normalne: 70+ zielony, 40-69 żółty, <40 czerwony
  // Inverted (humanization_risk): <30 zielony, 30-70 żółty, 70+ czerwony
  const good = inverted ? score < 30 : score >= 70;
  const bad = inverted ? score >= 70 : score < 40;
  const color = good
    ? "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30"
    : bad
      ? "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30"
      : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30";
  return (
    <div>
      <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
      <Badge className={`${color} border text-xs`}>{score}/100</Badge>
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

export function WpPostPreviewModal({ postId, onClose }: WpPostPreviewModalProps) {
  const [showFullPlain, setShowFullPlain] = useState(false);

  const post = useQuery({
    queryKey: ["wp-post-full", postId],
    queryFn: () => (postId ? fetchWpPostFull(postId) : Promise.resolve(null)),
    enabled: !!postId,
  });

  const matched = useQuery({
    queryKey: ["wp-post-matched-node", postId],
    queryFn: () => (postId ? fetchMatchedNode(postId) : Promise.resolve(null)),
    enabled: !!postId,
  });

  const open = !!postId;
  const p = post.data;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        {!p || post.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
            <div className="grid grid-cols-2 gap-6 pt-4">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg pr-8">{p.title}</DialogTitle>
              <div className="flex items-center gap-2 flex-wrap pt-2">
                <QualityBadge score={p.quality_score} recommendation={p.quality_recommendation} />
                <RecBadge rec={p.quality_recommendation} />
                {p.wp_url && (
                  <a
                    href={p.wp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1 ml-auto"
                  >
                    Otwórz w WordPress <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
              {/* LEFT — metadata */}
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Daty</p>
                  <p>
                    <strong>Opublikowano:</strong> {formatRel(p.published_at)}
                  </p>
                  <p>
                    <strong>Zmodyfikowano:</strong>{" "}
                    {p.modified_at === p.published_at ? (
                      <span className="text-muted-foreground/60">—</span>
                    ) : (
                      formatRel(p.modified_at)
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Metryki
                  </p>
                  <p>
                    {p.word_count ?? 0} słów · {p.h2_count ?? 0}×H2 · {p.h3_count ?? 0}×H3 ·{" "}
                    {p.image_count ?? 0} obrazów
                  </p>
                  <p>
                    Linki: {p.outbound_links_count ?? 0} wychodzących,{" "}
                    {p.internal_links_count ?? 0} wewnętrznych
                  </p>
                </div>

                {p.meta_description && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                      Meta description
                    </p>
                    <blockquote className="border-l-2 border-muted pl-3 italic text-muted-foreground">
                      {p.meta_description}
                    </blockquote>
                  </div>
                )}

                {p.featured_image_url && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                      Featured image
                    </p>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={p.featured_image_url}
                      alt={p.title}
                      className="max-w-full rounded border"
                      loading="lazy"
                    />
                  </div>
                )}

                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Problemy (pełna lista)
                  </p>
                  {p.quality_red_flags.length === 0 ? (
                    <p className="text-xs text-green-600/80">Brak zgłoszonych problemów</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {p.quality_red_flags.map((f) => (
                        <Badge key={f} variant="destructive" className="text-[10px]">
                          {QUALITY_FLAGS_LABELS[f] || f}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Keyword (ACF / ręczny)
                  </p>
                  <p className="text-sm">{p.keyword || <span className="text-muted-foreground/60">—</span>}</p>
                </div>

                {(p.categories.length > 0 || p.tags.length > 0) && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                      Taksonomie
                    </p>
                    {p.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        <span className="text-xs text-muted-foreground">Kategorie:</span>
                        {p.categories.map((c) => (
                          <Badge key={c.id} variant="secondary" className="text-[10px]">
                            {c.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {p.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        <span className="text-xs text-muted-foreground">Tagi:</span>
                        {p.tags.map((t) => (
                          <Badge key={t.id} variant="outline" className="text-[10px]">
                            {t.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RIGHT — content tabs */}
              <div>
                <Tabs defaultValue="preview" className="w-full">
                  <TabsList className="w-full">
                    <TabsTrigger value="preview" className="flex-1">
                      Preview
                    </TabsTrigger>
                    <TabsTrigger value="plain" className="flex-1">
                      Plain
                    </TabsTrigger>
                    <TabsTrigger value="sro" className="flex-1">
                      SRO
                    </TabsTrigger>
                    <TabsTrigger value="seo" className="flex-1">
                      SEO
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="preview" className="mt-3">
                    {p.content ? (
                      <iframe
                        title={`Preview: ${p.title}`}
                        sandbox=""
                        srcDoc={buildSafeSrcDoc(p.content)}
                        className="w-full rounded border bg-white"
                        style={{ height: 600 }}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground p-4">
                        Brak HTML zapisanego w DB. Uruchom sync.
                      </p>
                    )}
                  </TabsContent>

                  <TabsContent value="plain" className="mt-3">
                    {p.content_plaintext ? (
                      <>
                        <pre className="text-xs whitespace-pre-wrap bg-muted/30 rounded border p-3 max-h-[540px] overflow-y-auto">
                          {showFullPlain
                            ? p.content_plaintext
                            : p.content_plaintext.slice(0, 2000)}
                          {!showFullPlain && p.content_plaintext.length > 2000 && "…"}
                        </pre>
                        {p.content_plaintext.length > 2000 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2"
                            onClick={() => setShowFullPlain((v) => !v)}
                          >
                            {showFullPlain ? "Pokaż mniej" : `Pokaż wszystko (${p.content_plaintext.length} znaków)`}
                          </Button>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground p-4">
                        Brak plaintext. Uruchom sync.
                      </p>
                    )}
                  </TabsContent>

                  <TabsContent value="sro" className="mt-3 space-y-4">
                    {/* Output — analiza LLM */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        SRO Analysis (output LLM)
                      </p>
                      {p.sro_extracted_at ? (
                        <div className="border rounded bg-muted/20 p-3 space-y-2 text-sm">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground">Entity (główna encja)</p>
                              <p className="font-medium">{p.extracted_entity || <span className="text-muted-foreground/60">—</span>}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground">Attribute (wyciągany atrybut)</p>
                              <p className="font-medium">{p.extracted_attribute || <span className="text-muted-foreground/60">—</span>}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase text-muted-foreground mb-1">Query frame (intencja pytania)</p>
                            {p.extracted_query_frame && QUERY_FRAME_EXPLAIN[p.extracted_query_frame] ? (
                              <div className="flex items-center gap-2">
                                <Badge className={`${QUERY_FRAME_EXPLAIN[p.extracted_query_frame].color} border`}>
                                  {QUERY_FRAME_EXPLAIN[p.extracted_query_frame].label}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {QUERY_FRAME_EXPLAIN[p.extracted_query_frame].desc}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </div>
                          {p.first_paragraph_answer && (
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground mb-1">First paragraph answer</p>
                              <blockquote className="border-l-2 border-primary/40 pl-3 text-xs italic text-muted-foreground">
                                {p.first_paragraph_answer}
                              </blockquote>
                            </div>
                          )}
                          {p.supporting_entities && p.supporting_entities.length > 0 && (
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground mb-1">
                                Supporting entities ({p.supporting_entities.length})
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {p.supporting_entities.map((e, i) => (
                                  <Badge key={`${e}-${i}`} variant="secondary" className="text-[10px]">
                                    {e}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className="text-[10px] text-muted-foreground/70 pt-1">
                            Ekstrahowane: {formatRel(p.sro_extracted_at)}
                          </p>
                        </div>
                      ) : (
                        <div className="border border-dashed rounded p-4 text-center">
                          <p className="text-sm text-muted-foreground">
                            Brak ekstrakcji SRO. Uruchom "Wyekstrahuj SRO (LLM)" w panelu Istniejące wpisy.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* M12 — Quality Audit (LLM) */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Audyt jakości (LLM)
                      </p>
                      {p.quality_audited_at ? (
                        <div className="border rounded bg-muted/20 p-3 space-y-3 text-sm">
                          <div className="grid grid-cols-2 gap-3">
                            <AuditScore
                              label="Entity Anchoring"
                              score={p.entity_anchoring_score}
                              hint="≥70 = silne anchorowanie"
                            />
                            <AuditScore
                              label="Information Gain"
                              score={p.information_gain_score}
                              hint="konkretne liczby, unique angles"
                            />
                            <AuditScore
                              label="Humanization Risk"
                              score={p.humanization_risk_score}
                              hint="100=AI, 0=naturalne"
                              inverted
                            />
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground">
                                Retrieval Ready
                              </p>
                              <Badge
                                className={
                                  p.retrieval_ready
                                    ? "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30 border"
                                    : "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30 border"
                                }
                              >
                                {p.retrieval_ready ? "✓ pass" : "✗ fail"}
                              </Badge>
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                5-punktowy test SRO
                              </p>
                            </div>
                          </div>
                          {p.quality_audit_verdict && (
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground mb-1">
                                Werdykt
                              </p>
                              <p className="text-xs italic text-muted-foreground">
                                {p.quality_audit_verdict}
                              </p>
                            </div>
                          )}
                          {p.quality_audit_issues && p.quality_audit_issues.length > 0 && (
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground mb-1">
                                Krytyczne problemy ({p.quality_audit_issues.length})
                              </p>
                              <ul className="list-disc ml-4 space-y-0.5 text-xs text-muted-foreground">
                                {p.quality_audit_issues.map((issue, i) => (
                                  <li key={i}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <p className="text-[10px] text-muted-foreground/70 pt-1">
                            Audyt: {formatRel(p.quality_audited_at)}
                          </p>
                        </div>
                      ) : (
                        <div className="border border-dashed rounded p-4 text-center">
                          <p className="text-sm text-muted-foreground">
                            {p.sro_extracted_at
                              ? "Audyt jeszcze nie wykonany. Kliknij \"Audyt jakości (LLM)\" w panelu."
                              : "Najpierw wykonaj SRO extraction — audyt wymaga danych SRO jako kontekstu."}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* M14 — Cluster Fit (LLM scope classification) */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Cluster Fit (scope)
                      </p>
                      {p.cluster_fit ? (
                        <div className="border rounded bg-muted/20 p-3 space-y-2 text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                              className={
                                p.cluster_fit === "in_scope"
                                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 border"
                                  : p.cluster_fit === "adjacent"
                                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 border"
                                    : "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30 border"
                              }
                            >
                              {p.cluster_fit.replace("_", " ")}
                            </Badge>
                            {p.cluster_fit_confidence !== null && (
                              <span className="text-[11px] text-muted-foreground">
                                confidence: {p.cluster_fit_confidence.toFixed(2)}
                              </span>
                            )}
                            {p.cluster_fit_method && (
                              <span className="text-[10px] text-muted-foreground/70">
                                [{p.cluster_fit_method}]
                              </span>
                            )}
                          </div>
                          {p.cluster_fit_reason && (
                            <p className="text-xs italic text-muted-foreground">
                              {p.cluster_fit_reason}
                            </p>
                          )}
                          {p.cluster_fit_classified_at && (
                            <p className="text-[10px] text-muted-foreground/70">
                              Sklasyfikowano: {formatRel(p.cluster_fit_classified_at)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="border border-dashed rounded p-4 text-center">
                          <p className="text-sm text-muted-foreground">
                            {p.sro_extracted_at
                              ? 'Nie sklasyfikowano. Kliknij "Klasyfikuj scope (LLM)" w panelu.'
                              : "Najpierw wykonaj SRO extraction — klasyfikacja wymaga danych SRO."}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Input — pierwsze 100 słów */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Input do LLM (first_100_words)
                      </p>
                      {p.first_100_words ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none bg-muted/30 rounded border p-3 text-sm">
                          {p.first_100_words}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground p-4 border rounded border-dashed">
                          Brak — pole zostanie wypełnione przy najbliższym sync.
                        </p>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="seo" className="mt-3 space-y-4">
                    {/* Overview — agregaty z Senuto */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        SEO Overview (Senuto)
                      </p>
                      {p.seo_last_sync ? (
                        <div className="border rounded bg-muted/20 p-3 space-y-3">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground">Keywords (total)</p>
                              <p className="text-2xl font-bold tabular-nums">{p.seo_total_keywords ?? 0}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground">Traffic (est./mies)</p>
                              <p className="text-2xl font-bold tabular-nums">{p.seo_organic_traffic ?? 0}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground">Top 3 / 10 / 50</p>
                              <p className="text-base font-semibold tabular-nums">
                                <span className="text-emerald-600 dark:text-emerald-400">{p.seo_top_3_count ?? 0}</span>
                                <span className="text-muted-foreground"> / </span>
                                <span className="text-blue-600 dark:text-blue-400">{p.seo_top_10_count ?? 0}</span>
                                <span className="text-muted-foreground"> / </span>
                                <span>{p.seo_top_50_count ?? 0}</span>
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground">Gained / Lost</p>
                              <p className="text-base font-semibold tabular-nums">
                                <span className="text-emerald-600 dark:text-emerald-400">+{p.seo_gained_keywords_count ?? 0}</span>
                                <span className="text-muted-foreground"> / </span>
                                <span className="text-red-600 dark:text-red-400">−{p.seo_lost_keywords_count ?? 0}</span>
                              </p>
                            </div>
                          </div>

                          {p.seo_best_keyword && (
                            <div>
                              <p className="text-[10px] uppercase text-muted-foreground mb-1">Best keyword</p>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-primary/15 text-primary border-primary/30 border">
                                  pos #{p.seo_best_position}
                                </Badge>
                                <span className="font-medium">{p.seo_best_keyword}</span>
                                {p.seo_best_keyword_volume !== null && p.seo_best_keyword_volume > 0 && (
                                  <span className="text-xs text-muted-foreground">
                                    · vol {p.seo_best_keyword_volume}/mies
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {(p.seo_content_decay || p.seo_has_cannibalization) && (
                            <div className="flex flex-wrap gap-2">
                              {p.seo_content_decay && (
                                <Badge className="bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30 border">
                                  ⚠ Content decay (utracone keywords)
                                </Badge>
                              )}
                              {p.seo_has_cannibalization && (
                                <div className="flex flex-col gap-1">
                                  <Badge className="bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30 border">
                                    ⚠ Kanibalizacja — ten sam URL rankuje z innym
                                  </Badge>
                                  {p.seo_cannibalization_partner_url && (
                                    <a
                                      href={`https://${p.seo_cannibalization_partner_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[11px] text-primary hover:underline"
                                    >
                                      partner: /{p.seo_cannibalization_partner_url.split("/").slice(1).join("/")}
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          <p className="text-[10px] text-muted-foreground/70 pt-1">
                            Sync: {formatRel(p.seo_last_sync)}
                          </p>
                        </div>
                      ) : (
                        <div className="border border-dashed rounded p-4 text-center">
                          <p className="text-sm text-muted-foreground">
                            Brak sync Senuto. Kliknij "🔄 Sync SEO (Senuto)" w panelu lub odśwież wpisy (auto-trigger).
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Lista wszystkich keywordów */}
                    {p.seo_all_keywords && p.seo_all_keywords.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                          Keywords ({p.seo_all_keywords.length})
                        </p>
                        <div className="border rounded bg-muted/20 max-h-[320px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="text-left p-2">Keyword</th>
                                <th className="text-center p-2 w-[60px]">Pos</th>
                                <th className="text-center p-2 w-[60px]">Prev</th>
                                <th className="text-right p-2 w-[70px]">Vol/mies</th>
                                <th className="text-center p-2 w-[50px]">KD</th>
                                <th className="text-left p-2 w-[140px]">SERP features</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...p.seo_all_keywords]
                                .sort((a, b) => {
                                  if (a.position === 0 && b.position > 0) return 1;
                                  if (b.position === 0 && a.position > 0) return -1;
                                  return a.position - b.position;
                                })
                                .map((kw, i) => {
                                  const diff = kw.previous_position - kw.position;
                                  return (
                                    <tr key={`${kw.keyword}-${i}`} className="border-t">
                                      <td className="p-2">{kw.keyword}</td>
                                      <td className="p-2 text-center">
                                        <Badge
                                          className={
                                            kw.position === 0
                                              ? "bg-muted text-muted-foreground text-[10px]"
                                              : kw.position <= 3
                                                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 border text-[10px]"
                                                : kw.position <= 10
                                                  ? "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30 border text-[10px]"
                                                  : "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 border text-[10px]"
                                          }
                                        >
                                          {kw.position === 0 ? "—" : `#${kw.position}`}
                                        </Badge>
                                      </td>
                                      <td className="p-2 text-center text-muted-foreground tabular-nums">
                                        {kw.previous_position === 0 ? "—" : `#${kw.previous_position}`}
                                        {diff !== 0 && kw.previous_position > 0 && kw.position > 0 && (
                                          <span className={`ml-1 ${diff > 0 ? "text-emerald-600" : "text-red-600"}`}>
                                            ({diff > 0 ? "+" : ""}{diff})
                                          </span>
                                        )}
                                      </td>
                                      <td className="p-2 text-right tabular-nums">{kw.volume || "—"}</td>
                                      <td className="p-2 text-center tabular-nums text-muted-foreground">
                                        {kw.difficulty ?? "—"}
                                      </td>
                                      <td className="p-2">
                                        <div className="flex flex-wrap gap-1">
                                          {(kw.snippets || []).slice(0, 3).map((s) => (
                                            <Badge key={s} variant="outline" className="text-[9px]">
                                              {s.replace(/_/g, " ")}
                                            </Badge>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row sm:items-center gap-2">
              {matched.data && (
                <div className="text-xs text-muted-foreground inline-flex items-center gap-2 mr-auto">
                  <Link2 className="h-3.5 w-3.5" />
                  Zmatchowane z node:{" "}
                  <a
                    href={`/mapy-tematyczne/${matched.data.map_id}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {matched.data.title}
                  </a>
                </div>
              )}
              <Button variant="outline" onClick={onClose}>
                Zamknij
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
